import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import './gantt-styles.css';

const GanttChart: React.FC = () => {
  const { state } = useLocation() as { state: { data: any[]; title: string } | null };
  const { data = [], title = 'Gantt Chart' } = state || {};

  const ganttContainer = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [activeViewMode, setActiveViewMode] = useState('day');

  useEffect(() => {
    if (!ganttContainer.current) {
      console.warn('Gantt container not available');
      return;
    }

    if (isLoading) {
      return;
    }

    setIsLoading(true);

    // Configure Gantt for optimal performance
    gantt.config.scroll_size = 20;
    gantt.config.scroll_sensitivity = 20;
    gantt.config.scroll_speed = 20;
    gantt.config.smart_scroll = true;
    gantt.config.smart_rendering = true;
    gantt.config.virtual_scroll = true;
    gantt.config.virtual_scroll_unit = 'day';
    gantt.config.virtual_scroll_step = 30;

    // Timeline configuration
    gantt.config.scale_height = 50;
    gantt.config.row_height = 40;
    gantt.config.grid_width = 300; // Increased width for task names
    gantt.config.min_column_width = 30;

    // Task list configuration - disable tree structure
    gantt.config.show_task_cells = false; // Removes expand/collapse icons
    gantt.config.show_grid = true;
    gantt.config.show_links = true;

    // Configure task list columns
    gantt.config.columns = [
      { name: 'text', label: 'Task Name', width: 300, tree: false },
      { name: 'start_date', label: 'Start', width: 100, align: 'center' },
      { name: 'end_date', label: 'End', width: 100, align: 'center' }
      // {
      //   name: 'progress',
      //   label: 'Progress',
      //   width: 80,
      //   align: 'center',
      //   template: function (task) {
      //     return Math.round((task.progress || 0) * 100) + '%';
      //   }
      // }
    ];

    // Performance optimizations
    gantt.config.auto_scheduling = false;
    gantt.config.auto_scheduling_strict = false;
    gantt.config.auto_scheduling_compatibility = false;
    gantt.config.auto_scheduling_use_progress = false;

    // Essential configuration for rendering
    gantt.config.show_links = true;
    gantt.config.show_grid = true;
    gantt.config.show_task_cells = false; // Disable expand/collapse icons
    gantt.config.show_quick_info = true;

    // Task name display configuration
    gantt.config.task_text = function (task) {
      return task.text || task.name || '';
    };

    // Disable task tree functionality
    gantt.config.show_task_cells = false;
    gantt.config.show_grid = true;

    // Disable all drag functionality for read-only mode
    gantt.config.drag_links = false;
    gantt.config.drag_progress = false;
    gantt.config.drag_resize = false;
    gantt.config.drag_move = false;

    // Disable task editing
    gantt.config.readonly = true;
    gantt.config.editable = false;

    // Link configuration
    gantt.config.link_arrow_offset = 5;
    gantt.config.link_arrow_size = 8;
    gantt.config.link_line_width = 2;
    gantt.config.link_arrow_angle = 45;

    // View modes
    gantt.config.scales = [
      { unit: 'month', step: 1, format: '%F %Y' },
      { unit: 'day', step: 1, format: '%j' }
    ];

    // Set date format
    gantt.config.date_format = '%Y-%m-%d';

    // Task styling
    gantt.templates.task_class = function (start, end, task) {
      if (task.progress === 0) return 'task_no_progress';
      if (task.progress === 100) return 'task_completed';
      return 'task_in_progress';
    };

    // Transform data to DHTMLX format with proper date handling
    const transformedData = data.map((item, index) => {
      // Helper function to ensure valid date
      const getValidDate = (dateValue: any, fallbackDays: number = 0) => {
        if (!dateValue) {
          return new Date(Date.now() + fallbackDays * 24 * 60 * 60 * 1000);
        }

        const date = new Date(dateValue);
        if (isNaN(date.getTime())) {
          return new Date(Date.now() + fallbackDays * 24 * 60 * 60 * 1000);
        }

        return date;
      };

      const startDate = getValidDate(
        item.planned_start_date || item.StartDate || item.start_date,
        0
      );

      const endDate = getValidDate(item.planned_end_date || item.EndDate || item.end_date, 7);

      // Ensure end date is after start date
      const finalEndDate =
        endDate <= startDate ? new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000) : endDate;

      return {
        id: item.TaskID || item.id || index + 1,
        text: item.TaskName || item.name || item.title || `Task ${index + 1}`,
        start_date: startDate,
        end_date: finalEndDate,
        progress: Math.max(0, Math.min(1, (item.Progress || item.progress || 0) / 100))
        // Removed parent, open, type properties to prevent tree structure
      };
    });

    // Process dependencies to create links
    const links: any[] = [];
    data.forEach((item) => {
      if (item.dependencies && Array.isArray(item.dependencies)) {
        item.dependencies.forEach((dep: any) => {
          if (dep.dependency_id && dep.task_id) {
            links.push({
              id: `link_${dep.task_id}_${dep.dependency_id}`,
              source: dep.dependency_id,
              target: dep.task_id,
              type: '0' // Finish-to-Start dependency
            });
          }
        });
      }
    });

    console.log('Processed links:', links);

    // Debug: Log original and transformed data
    console.log('Original data:', data);
    console.log('Transformed Gantt Data:', transformedData);

    // Validate data before initialization
    const validData = transformedData.filter((task) => {
      const isValid =
        task.start_date instanceof Date &&
        task.end_date instanceof Date &&
        !isNaN(task.start_date.getTime()) &&
        !isNaN(task.end_date.getTime());
      if (!isValid) {
        console.warn('Invalid task data:', task);
      }
      return isValid;
    });

    console.log(`Valid tasks: ${validData.length}/${transformedData.length}`);

    // If no valid data, create sample data for testing
    if (validData.length === 0) {
      console.log('No valid data found, creating sample data...');
      const sampleData = [
        {
          id: 1,
          text: 'Sample Task 1',
          start_date: new Date(),
          end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          progress: 0.5,
          parent: 0,
          open: true,
          type: 'task'
        },
        {
          id: 2,
          text: 'Sample Task 2',
          start_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
          end_date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
          progress: 0.3,
          parent: 0,
          open: true,
          type: 'task'
        }
      ];
      console.log('Using sample data:', sampleData);
      validData.push(...sampleData);
    }

    // Initialize Gantt with proper data structure
    try {
      gantt.init(ganttContainer.current);

      // Clear any existing data first
      gantt.clearAll();

      // Parse data with proper structure including links
      gantt.parse({
        data: validData,
        links: links
      });

      // Force render to ensure display
      gantt.render();

      console.log('Gantt chart initialized successfully');
    } catch (error) {
      console.error('Error initializing Gantt chart:', error);
      setHasError(true);
      // Fallback: Initialize with minimal structure
      try {
        gantt.clearAll();
        gantt.parse({
          data: [],
          links: []
        });
        gantt.render();
        console.log('Fallback initialization completed');
        setHasError(false);
      } catch (fallbackError) {
        console.error('Fallback initialization failed:', fallbackError);
        setHasError(true);
      }
    } finally {
      setIsLoading(false);
    }

    // Add zoom controls with proper error handling
    const zoomIn = () => {
      try {
        setZoomLevel((prev) => {
          const newLevel = Math.min(3, prev + 0.5);

          // Adjust column width based on zoom level
          gantt.config.min_column_width = Math.min(100, 30 + (newLevel - 1) * 20);

          // Adjust scale step for better zoom
          if (gantt.config.scales && gantt.config.scales.length > 1) {
            const dayScale = gantt.config.scales[1];
            if (dayScale) {
              dayScale.step = Math.max(1, Math.floor(3 - newLevel));
            }
          }

          gantt.render();
          return newLevel;
        });
      } catch (error) {
        console.error('Zoom in error:', error);
      }
    };

    const zoomOut = () => {
      try {
        setZoomLevel((prev) => {
          const newLevel = Math.max(0.5, prev - 0.5);

          // Adjust column width based on zoom level
          gantt.config.min_column_width = Math.max(20, 30 + (newLevel - 1) * 20);

          // Adjust scale step for better zoom
          if (gantt.config.scales && gantt.config.scales.length > 1) {
            const dayScale = gantt.config.scales[1];
            if (dayScale) {
              dayScale.step = Math.min(7, Math.floor(3 - newLevel) + 1);
            }
          }

          gantt.render();
          return newLevel;
        });
      } catch (error) {
        console.error('Zoom out error:', error);
      }
    };

    const resetZoom = () => {
      try {
        setZoomLevel(1);
        gantt.config.min_column_width = 30;

        // Reset to default scales
        gantt.config.scales = [
          { unit: 'month', step: 1, format: '%F %Y' },
          { unit: 'day', step: 1, format: '%j' }
        ];

        gantt.render();
      } catch (error) {
        console.error('Reset zoom error:', error);
      }
    };

    const setViewMode = (mode: string) => {
      try {
        setActiveViewMode(mode);

        switch (mode) {
          case 'day':
            gantt.config.scales = [
              { unit: 'month', step: 1, format: '%F %Y' },
              { unit: 'day', step: 1, format: '%j' }
            ];
            break;
          case 'week':
            gantt.config.scales = [
              { unit: 'month', step: 1, format: '%F %Y' },
              { unit: 'week', step: 1, format: 'Week %W' }
            ];
            break;
          case 'month':
            gantt.config.scales = [
              { unit: 'year', step: 1, format: '%Y' },
              { unit: 'month', step: 1, format: '%F' }
            ];
            break;
        }
        gantt.render();
      } catch (error) {
        console.error('Set view mode error:', error);
      }
    };

    // Store functions globally for button access
    (window as any).ganttZoomIn = zoomIn;
    (window as any).ganttZoomOut = zoomOut;
    (window as any).ganttResetZoom = resetZoom;
    (window as any).ganttSetViewMode = setViewMode;

    return () => {
      try {
        if (ganttContainer.current) {
          gantt.clearAll();
          gantt.destructor();
        }
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    };
  }, [data]);

  return (
    <div
      style={{
        background: '#fff',
        padding: 24,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
      {/* Header with title and controls */}
      <div style={{ marginBottom: 20, flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
          <h1 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>{title}</h1>

          {/* Status Badge */}
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '20px',
              background: '#e3f2fd',
              border: '1px solid #1976d2',
              fontSize: '12px',
              color: '#1976d2',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
            <span>📊</span>
            <span>{activeViewMode.charAt(0).toUpperCase() + activeViewMode.slice(1)} View</span>
            <span>•</span>
            <span>🔍</span>
            <span>{Math.round(zoomLevel * 100)}%</span>
          </div>
        </div>

        {/* View Controls */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={() => (window as any).ganttSetViewMode('day')}
            style={{
              padding: '8px 16px',
              border: `1px solid ${activeViewMode === 'day' ? '#1976d2' : '#ddd'}`,
              borderRadius: '4px',
              background: activeViewMode === 'day' ? '#1976d2' : '#fff',
              color: activeViewMode === 'day' ? '#fff' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeViewMode === 'day' ? '600' : '400',
              transition: 'all 0.2s ease'
            }}>
            📅 Day
          </button>
          <button
            onClick={() => (window as any).ganttSetViewMode('week')}
            style={{
              padding: '8px 16px',
              border: `1px solid ${activeViewMode === 'week' ? '#1976d2' : '#ddd'}`,
              borderRadius: '4px',
              background: activeViewMode === 'week' ? '#1976d2' : '#fff',
              color: activeViewMode === 'week' ? '#fff' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeViewMode === 'week' ? '600' : '400',
              transition: 'all 0.2s ease'
            }}>
            📊 Week
          </button>
          <button
            onClick={() => (window as any).ganttSetViewMode('month')}
            style={{
              padding: '8px 16px',
              border: `1px solid ${activeViewMode === 'month' ? '#1976d2' : '#ddd'}`,
              borderRadius: '4px',
              background: activeViewMode === 'month' ? '#1976d2' : '#fff',
              color: activeViewMode === 'month' ? '#fff' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeViewMode === 'month' ? '600' : '400',
              transition: 'all 0.2s ease'
            }}>
            📆 Month
          </button>
          <button
            onClick={() => (window as any).ganttZoomIn()}
            style={{
              padding: '8px 16px',
              border: '1px solid #4caf50',
              borderRadius: '4px',
              background: '#4caf50',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}>
            🔍+ Zoom In
          </button>
          <button
            onClick={() => (window as any).ganttZoomOut()}
            style={{
              padding: '8px 16px',
              border: '1px solid #ff9800',
              borderRadius: '4px',
              background: '#ff9800',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}>
            🔍- Zoom Out
          </button>
          <button
            onClick={() => (window as any).ganttResetZoom()}
            style={{
              padding: '8px 16px',
              border: '1px solid #9e9e9e',
              borderRadius: '4px',
              background: '#9e9e9e',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}>
            ↺ Reset
          </button>
        </div>
      </div>

      {/* Gantt Chart Container */}
      <div
        ref={ganttContainer}
        className="gantt_container"
        style={{
          flex: 1,
          height: 0,
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fafafa',
          position: 'relative'
        }}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
          <div style={{ fontSize: '16px', color: '#666' }}>Loading Gantt Chart...</div>
        </div>
      )}

      {/* Error State */}
      {hasError && !isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
          <div style={{ fontSize: '18px', color: '#d32f2f', marginBottom: '10px' }}>
            ⚠️ Gantt Chart Error
          </div>
          <div
            style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '20px' }}>
            Unable to initialize the Gantt chart. Please check the console for details.
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
            Reload Page
          </button>
        </div>
      )}
    </div>
  );
};

export default GanttChart;
